import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import PasswordInput from '../../components/common/PasswordInput';
import '../../assets/styles/auth.css';

const Register = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Organisation data
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('developer');
  
  // User data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const validateStep1 = () => {
    if (!orgName.trim()) {
      setError('Organisation name is required');
      return false;
    }
    if (!orgType) {
      setError('Please select an organisation type');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return false;
    }
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      const result = await register(
        { name: orgName, type: orgType },
        { email, password, firstName, lastName }
      );
      
      if (result.success) {
        toast.success('Registration successful! Please check your email to verify your account before signing in.');
        navigate('/login', { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    if (!password) return { strength: 0, text: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    
    const texts = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    return { strength, text: texts[strength] };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Form Section */}
        <div className="auth-form-section">
          <Link to="/" className="auth-logo">
            <i className="fas fa-shield-alt"></i>
            <span>SafeWork<span className="brand-360">360</span></span>
          </Link>

          {/* Steps Indicator */}
          <div className="auth-steps">
            <div className="auth-step">
              <div className={`step-number ${step === 1 ? 'active' : 'completed'}`}>
                {step > 1 ? <i className="fas fa-check"></i> : '1'}
              </div>
              <span className={`step-label ${step === 1 ? 'active' : ''}`}>Organisation</span>
            </div>
            <div className={`step-connector ${step > 1 ? 'completed' : ''}`}></div>
            <div className="auth-step">
              <div className={`step-number ${step === 2 ? 'active' : 'inactive'}`}>2</div>
              <span className={`step-label ${step === 2 ? 'active' : ''}`}>Your Details</span>
            </div>
          </div>

          {error && (
            <div className="auth-alert error">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Organisation */}
          {step === 1 && (
            <>
              <div className="auth-header">
                <h1>Register your organisation</h1>
                <p>Start your 14-day free trial. No credit card required.</p>
              </div>

              <form className="auth-form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
                <div className="form-group">
                  <label htmlFor="orgName">Organisation Name</label>
                  <input
                    type="text"
                    id="orgName"
                    className="form-control"
                    placeholder="e.g. ACME Construction Ltd"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Organisation Type</label>
                  <div className="form-options">
                    <label 
                      className={`form-option ${orgType === 'developer' ? 'selected' : ''}`}
                      onClick={() => setOrgType('developer')}
                    >
                      <input 
                        type="radio" 
                        name="orgType" 
                        value="developer"
                        checked={orgType === 'developer'}
                        onChange={(e) => setOrgType(e.target.value)}
                      />
                      <div className="form-option-content">
                        <h4>Organisation / Implementer</h4>
                        <p>Any organisation running its own projects or programs, including construction, NGOs, CBOs, government, healthcare, education, and more</p>
                      </div>
                    </label>
                    
                    <label 
                      className={`form-option ${orgType === 'consultant' ? 'selected' : ''}`}
                      onClick={() => setOrgType('consultant')}
                    >
                      <input 
                        type="radio" 
                        name="orgType" 
                        value="consultant"
                        checked={orgType === 'consultant'}
                        onChange={(e) => setOrgType(e.target.value)}
                      />
                      <div className="form-option-content">
                        <h4>Consultant / Service Provider</h4>
                        <p>Firms or professionals managing compliance across multiple client organisations</p>
                      </div>
                    </label>
                  </div>
                </div>

                <button type="submit" className="btn-auth-primary">
                  Continue
                  <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </form>
            </>
          )}

          {/* Step 2: User Details */}
          {step === 2 && (
            <>
              <button type="button" className="btn-back" onClick={handleBack}>
                <i className="fas fa-arrow-left"></i>
                Back
              </button>

              <div className="auth-header">
                <h1>Create your account</h1>
                <p>You'll be the administrator for {orgName}</p>
              </div>

              <form className="auth-form" onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      className="form-control"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      className="form-control"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Work Email</label>
                  <div className="input-icon-wrapper">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      placeholder="john@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <PasswordInput
                    leftIcon="fas fa-lock"
                    wrapperClassName="input-icon-wrapper"
                    className="form-control"
                    id="password"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  {password && (
                    <div className="password-strength">
                      <div className="strength-bar">
                        {[1, 2, 3, 4].map((level) => (
                          <div 
                            key={level}
                            className={`strength-segment ${
                              passwordStrength.strength >= level 
                                ? ['weak', 'fair', 'good', 'strong'][passwordStrength.strength - 1]
                                : ''
                            }`}
                          />
                        ))}
                      </div>
                      <span className="strength-text">{passwordStrength.text}</span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <PasswordInput
                    leftIcon="fas fa-lock"
                    wrapperClassName="input-icon-wrapper"
                    className="form-control"
                    id="confirmPassword"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <button type="submit" className="btn-auth-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm"></span>
                      Creating account...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rocket"></i>
                      Create Account
                    </>
                  )}
                </button>

                <p className="text-center text-muted" style={{ fontSize: '0.8rem', marginTop: '1rem' }}>
                  By creating an account, you agree to our{' '}
                  <a href="#terms" className="forgot-link">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#privacy" className="forgot-link">Privacy Policy</a>
                </p>
              </form>
            </>
          )}

          <div className="auth-footer">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>

        {/* Features Section */}
        <div className="auth-features-section">
          <div className="features-content">
            <h2>Start monitoring in minutes</h2>
            <p>Built for any organisation running projects on the ground, including construction firms, NGOs, CBOs, and government programs, to manage their compliance in one place.</p>
            
            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Quick Setup</h4>
                  <p>Get started in under 5 minutes with guided onboarding</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-globe"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Regional Compliance</h4>
                  <p>Pre-configured for multiple regulatory frameworks</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-mobile-alt"></i>
                </div>
                <div className="feature-item-text">
                  <h4>Mobile Ready</h4>
                  <p>Access from anywhere on any device</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-item-icon">
                  <i className="fas fa-headset"></i>
                </div>
                <div className="feature-item-text">
                  <h4>24/7 Support</h4>
                  <p>Get help whenever you need it</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
